<template>
  <div class="o-filters__modes">
    <div class="o-filters__search">
      <input
        class="o-filters__search-input"
        v-model.trim="name"
        autocomplete="gathering-name business-name"
        :placeholder="$t('filter-business-gathering-by-name-placeholder')"
        @input="setFilterName"
      />
      <span class="o-filters__search-begin" v-if="name.length <= 0" />
      <button type="button" class="o-filters__search-erase" v-else @click="clearSearch" />
    </div>

    <button class="o-filters__btn-filters" @click="openFilterDialog">
      <span class="filter-count" v-if="appliedFilterCount > 0">{{ appliedFilterCount }}</span>
      {{ $t('filters-btn') }}
      <span class="remove-all-filters-btn" v-if="appliedFilterCount > 0" @click.stop="removeAllFilters" />
    </button>

    <generic-popup v-model="filtersOpen" :title="$t('filters-btn')">
      <div class="m-pills" v-if="pills.length > 0">
        <div class="m-pills__pill-stash">
          <pill v-for="pill in pills" :key="pill.id" :label="pill.label" @close="removeFilter(pill.type, pill.id)" />
        </div>
        <button class="m-pills__reset-btn" @click="removeAllFilters">{{ $t('filter-remove-all') }}</button>
      </div>

      <ul class="m-filters m-filters--main">
        <li class="m-filters__item m-filters__item--bold" v-if="showGlobalCheckboxes">
          <span class="a-form-checkbox a-form-checkbox--block">
            <input type="checkbox" id="showGatherings" v-model="filters.showGatherings" />
            <label for="showGatherings">{{ $t('tab-list-gatherings') }}</label>
          </span>
        </li>

        <li class="m-filters__item m-filters__item--bold" v-if="showGlobalCheckboxes">
          <span class="a-form-checkbox a-form-checkbox--block">
            <input type="checkbox" id="showBusinesses" v-model="filters.showBusinesses" />
            <label for="showBusinesses">{{ $t('tab-list-businesses') }}</label>
          </span>
        </li>

        <li class="m-filters__item m-filters__item--bold m-filters__item--parent">
          <div class="m-filters__arrow">
            {{ $t('filter-group-category') }}
            <span :class="'arrow ' + (showCategories ? 'arrow__open' : '')" @click="toggleShowCategorySubFilters" />
          </div>

          <ul class="m-filters m-filters--child" v-if="showCategories">
            <li v-for="filter in categoryFilters" :key="filter.id" class="m-filters__item m-filters__item--bold">
              <span class="a-form-checkbox a-form-checkbox--block">
                <input type="checkbox" :id="filter.id" v-model="filters.categories[filter.id].checked" />
                <label :for="filter.id">{{ filter.label }}</label>
              </span>
            </li>
          </ul>
        </li>

        <li class="m-filters__item m-filters__item--bold m-filters__item--parent">
          <div class="m-filters__arrow">
            {{ $t('filter-group-region-and-city') }}
            <span :class="'arrow ' + (showRegions ? 'arrow__open' : '')" @click="toggleShowRegionSubFilters" />
          </div>
          <ul class="m-filters m-filters--child" v-if="showRegions">
            <checkbox-group v-for="filter in regionFilters" :key="filter.id">
              <template v-slot:parent>
                <span class="a-form-checkbox a-form-checkbox--block">
                  <input type="checkbox" :id="filter.id" v-model="filters.regions[filter.id].checked" @change="toggleCities(filters.regions[filter.id])" />
                  <label :for="filter.id">{{ filter.label }}</label>
                </span>
              </template>
              <template v-slot:children>
                <span v-for="cityFilter in getSortedCityFiltersForRegionId(filter.id)" :key="cityFilter.id" class="a-form-checkbox a-form-checkbox--block">
                  <input type="checkbox" :id="cityFilter.id" v-model="filters.cities[cityFilter.id].checked" @change="toggleRegion(filters.cities[cityFilter.id])" />
                  <label :for="cityFilter.id">{{ cityFilter.label }}</label>
                </span>
              </template>
            </checkbox-group>
          </ul>
        </li>
      </ul>

      <div class="filters__footer">
        <button class="a-btn-primary" @click="closeFiltersDialog">{{ $t('filters-apply-btn') }}</button>
      </div>
    </generic-popup>
  </div>
</template>

<script>
import GenericPopup from '@/components/popup/generic-popup'
import CheckboxGroup from '@/components/pages/encourage/components/checkbox-group'
import Pill from '@/components/ui/pill'

import { FILTER_HIDE_GATHERINGS, FILTER_HIDE_BUSINESSES, FILTER_CATEGORIES, FILTER_REGIONS, FILTER_CITIES } from '@/consts/filters'

export default {
  components: {
    GenericPopup,
    CheckboxGroup,
    Pill
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    showGlobalCheckboxes: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      name: '',
      filtersOpen: false,
      showCategories: false,
      showRegions: false
    }
  },
  computed: {
    filters: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    pills: function() {
      const pills = []

      if (this.filters) {
        if (this.showGlobalCheckboxes) {
          if (!this.filters.showGatherings) {
            pills.push(this.createPill(FILTER_HIDE_GATHERINGS, FILTER_HIDE_GATHERINGS, this.$t('filter-hide-gatherings')))
          }

          if (!this.filters.showBusinesses) {
            pills.push(this.createPill(FILTER_HIDE_BUSINESSES, FILTER_HIDE_BUSINESSES, this.$t('filter-hide-businesses')))
          }
        }

        this.categoryFilters.map(filter => {
          if (filter.checked) {
            pills.push(this.createPill(FILTER_CATEGORIES, filter.id, filter.name))
          }
        })

        this.regionFilters.map(filter => {
          if (filter.checked) {
            pills.push(this.createPill(FILTER_REGIONS, filter.id, filter.name))
          } else {
            this.getSortedCityFiltersForRegionId(filter.id).map(cityFilter => {
              if (cityFilter.checked) {
                pills.push(this.createPill(FILTER_CITIES, cityFilter.id, cityFilter.name))
              }
            })
          }
        })
      }

      return pills
    },
    appliedFilterCount: function() {
      return this.pills.length
    },
    categoryFilters: function() {
      return this.filters ? this.getSortedFilters(this.filters.categories) : []
    },
    regionFilters: function() {
      return this.filters ? this.getSortedFilters(this.filters.regions) : []
    }
  },
  methods: {
    createPill: function(type, id, label, parentId = null) {
      return { type: type, id: id, label: label }
    },
    getSortedFilters: function(filters) {
      const sortedFilters = []

      for (let filterId in filters) {
        const filter = filters[filterId]
        sortedFilters.push({ id: filterId, name: filter.name, label: this.getLabelWithCount(filter.name, filter), checked: filter.checked })
      }

      return sortedFilters.sort((a, b) => {
        if (a.name < b.name) {
          return -1
        }
        if (a.name > b.name) {
          return 1
        }
        return 0
      })
    },
    getLabelWithCount: function(label, filter) {
      let count = this.filters.showGatherings ? filter.gatheringIds.size : 0
      count += this.filters.showBusinesses ? filter.businessIds.size : 0
      return `${label} (${count})`
    },
    getSortedCityFiltersForRegionId: function(regionId) {
      const filters = {}

      for (let cityRegionId in this.filters.cities) {
        if (this.filters.cities[cityRegionId].regionId === regionId) {
          filters[cityRegionId] = this.filters.cities[cityRegionId]
        }
      }

      return this.getSortedFilters(filters)
    },
    setFilterName: function() {
      this.$emit('input', { ...this.value, name: this.name.toLowerCase() })
    },
    removeFilter: function(type, id) {
      if (type !== FILTER_HIDE_GATHERINGS && type !== FILTER_HIDE_BUSINESSES) {
        this.filters[type][id].checked = false

        if (type === FILTER_REGIONS) {
          this.toggleCities(this.filters[type][id])
        } else {
          this.$emit('input', { ...this.value, [type]: this.filters[type] })
        }
      } else {
        this.filters[type] = true
        this.$emit('input', { ...this.value, [type]: this.filters[type] })
      }
    },
    removeAllFilters: function() {
      this.filters.showGatherings = true
      this.filters.showBusinesses = true

      for (let filter in this.filters.categories) {
        this.filters.categories[filter].checked = false
      }
      for (let filter in this.filters.regions) {
        this.filters.regions[filter].checked = false
      }
      for (let filter in this.filters.cities) {
        this.filters.cities[filter].checked = false
      }
      this.$emit('input', { ...this.value, categories: this.filters.categories })
      this.$emit('input', { ...this.value, regions: this.filters.regions })
      this.$emit('input', { ...this.value, cities: this.filters.cities })
    },
    toggleShowCategorySubFilters: function() {
      this.showCategories = !this.showCategories
    },
    toggleShowRegionSubFilters: function() {
      this.showRegions = !this.showRegions
    },
    toggleCities(region) {
      for (let cityRegionId in this.filters.cities) {
        if (this.filters.cities[cityRegionId].regionId === region.id) {
          this.filters.cities[cityRegionId].checked = region.checked
        }
      }
      this.$emit('input', { ...this.value, regions: this.filters.regions })
      this.$emit('input', { ...this.value, cities: this.filters.cities })
    },
    toggleRegion(currentCity) {
      if (currentCity.checked) {
        let checkRegion = true
        for (let cityRegionId in this.filters.cities) {
          if (this.filters.cities[cityRegionId].regionId === currentCity.regionId && !this.filters.cities[cityRegionId].checked) {
            checkRegion = false
            break
          }
        }
        this.filters.regions[currentCity.regionId].checked = checkRegion
      } else {
        this.filters.regions[currentCity.regionId].checked = false
      }
      this.$emit('input', { ...this.value, regions: this.filters.regions })
      this.$emit('input', { ...this.value, cities: this.filters.cities })
    },
    openFilterDialog: function() {
      this.filtersOpen = true
    },
    closeFiltersDialog: function() {
      this.filtersOpen = false
    },
    clearSearch: function() {
      this.name = ''
      this.setFilterName()
    }
  }
}
</script>

<style lang="scss">
.m-filters {
  list-style-type: none;
  padding: 0;
  margin: 0;

  &--main {
    border-top: 1px solid #e9e8e6;
  }

  &--child {
    font-size: $font-size-base - 2px;

    .m-filters__item:first-child {
      border-top: 1px solid #e9e8e6;
    }

    .m-filters__item:last-child {
      border-bottom: 0 solid transparent;
    }
  }

  &__item {
    border-bottom: 1px solid #e9e8e6;
    padding: ($margin-base / 2) 0;

    &--bold {
      font-weight: 800;
    }

    /*In this case the element __arrow manage the padding*/
    &--parent {
      padding: 0;
    }

    &--deep-child {
      padding-left: spacing('light');
      font-weight: normal;
    }
  }

  &__arrow {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    padding: ($margin-base / 2) 0;
  }
}

.arrow {
  cursor: pointer;
  display: inline-block;
  background: transparent url(~@/assets/arrow.svg) no-repeat center center;
  height: 16px;
  width: 40px;
  vertical-align: middle;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 40px;
    height: 40px;
    transform: translate(0, -50%);
    top: 50%;
    right: 0;
    border: 1px solid transparent;
    border-radius: 8px;
    transition: border-color 0.25s ease;
  }

  &:hover {
    &::before {
      border-color: color('color1');
    }
  }

  &__open {
    transform: rotate(180deg);
  }
}

.filters__footer {
  padding: spacing('light') 0 0;
}

.filter-count {
  display: block;
  background-color: #fff;
  color: #7667a7;
  border-radius: 15px;
  width: 22px;
  height: 22px;
  font: bold 14px/14px Helvetica, Verdana, Tahoma;
  text-align: center;
  padding: 3px;
  margin-right: spacing('thin');
  flex-shrink: 0;

  position: absolute;
  z-index: 2;
  border: 1px solid color('primary');
  top: -5px;
  left: -5px;
}

.remove-all-filters-btn {
  display: none;
  cursor: pointer;
  height: 16px;
  width: 16px;
  background: transparent url(~@/assets/close.svg) no-repeat center center;
  background-size: contain;
  margin-left: spacing('thin');
  position: relative;
  flex-shrink: 0;
  &::before {
    content: '';
    width: 30px;
    height: 30px;
    border-radius: 4px;
    color: transparent;
    position: absolute;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
    border: 1px solid transparent;
    transition: border-color 0.25s ease;
  }
  &:hover {
    &::before {
      border-color: color('white');
    }
  }
}

.filter-type {
  cursor: pointer;
}

.remove-all {
  cursor: pointer;
  color: #7667a7;
  text-decoration: underline;
}

@media screen and (min-width: min-width('xs')) {
  .filter-count {
    position: static;
    border: 0 solid transparent;
    padding: 4px 3px 0;
  }

  .remove-all-filters-btn {
    display: block;
  }
}
</style>
